import { useRoute } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  FlatList,
  Image,
  ListRenderItem,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import DynamicListItem from './ListItem';

import { usePlayer } from 'components/Player/context';

type DynamicListProps = {
  items: CmsHomeModule[];
  refreshControl?: ReactElement;
  initialNumToRender?: number;
  maxToRenderPerBatch?: number;
  initialNumToLoad?: number;
  maxToLoadPerBatch?: number;
};

const DynamicList: React.FC<DynamicListProps> = ({
  items,
  refreshControl,
  initialNumToRender = 3,
  maxToRenderPerBatch = 3,
  initialNumToLoad = 5,
  maxToLoadPerBatch = 1,
}) => {
  const { handleScroll, offSetScroll, PromotionArea } = useGlobalConfig();
  const { name: routeName } = useRoute();

  const itemsWithSuggestionSection: CmsHomeModule[] = React.useMemo(() => {
    if (routeName !== 'Home') return items;

    const keepWatchingIndex = items.findIndex((item) => item._name === 'sectionWatchingList');
    return [
      ...items.slice(0, keepWatchingIndex),
      { _name: 'sectionSuggestion' },
      ...items.slice(keepWatchingIndex),
    ];
  }, [items, routeName]);
  const { isKid } = useSelector((state: RootState) => state.profile);
  const [end, setEnd] = useState(Math.min(itemsWithSuggestionSection.length, initialNumToLoad));
  const dataItems = itemsWithSuggestionSection.slice(0, end);
  const { width, height } = useWindowDimensions();
  const { pause, play, isFullScreen } = usePlayer();
  const wasPlayingRef = useRef(false);

  const isPromotionActive = PromotionArea?.is_active && !Platform.isTV && !isKid;
  const isWeb = Platform.OS === 'web';
  const isSmallScreen = height <= 700;

  const hasBanner = routeName === 'Home';

  const renderItem: ListRenderItem<CmsHomeModule> = ({ item }) => (
    <View style={styles.itemWrapper}>
      <DynamicListItem {...item} />
    </View>
  );

  const flatListRef = useRef<FlatList<any>>(null);

  useEffect(() => {
    if (Platform.OS === 'web' && flatListRef.current && routeName === 'Home') {
      const scrollEl = flatListRef.current.getScrollableNode?.() ?? flatListRef.current;
      const scrollThreshold = width < 720 ? 230 : 570;

      const handleScrollWeb = (e: Event) => {
        const scrollY = (e.target as HTMLElement).scrollTop;

        const videos = document.querySelectorAll('video');
        let hasPlayingVideo = false;

        videos.forEach((video) => {
          if (!video.paused && video.currentTime > 0 && !video.ended) {
            hasPlayingVideo = true;
          }
        });

        if (scrollY > scrollThreshold) {
          if (hasPlayingVideo) {
            videos.forEach((video) => {
              try {
                video.pause();
              } catch (err) {}
            });

            pause();
            wasPlayingRef.current = true;
          }
        } else if (wasPlayingRef.current) {
          videos.forEach((video) => {
            try {
              video.play().catch(() => {});
            } catch (err) {}
          });

          play();
          wasPlayingRef.current = false;
        } else if (scrollY === 0) {
          let hasPausedVideo = false;
          videos.forEach((video) => {
            if (video.paused && video.currentTime > 0 && !video.ended && !wasPlayingRef.current) {
              hasPausedVideo = true;
            }
          });

          if (hasPausedVideo) {
            videos.forEach((video) => {
              try {
                video.play().catch(() => {});
              } catch (err) {}
            });

            play();
          }
        }
      };

      scrollEl.addEventListener('scroll', handleScrollWeb);
      return () => scrollEl.removeEventListener('scroll', handleScrollWeb);
    }
  }, [width]);

  useEffect(() => {
    flatListRef.current?.scrollToOffset({
      offset: offSetScroll!,
      animated: true,
    });
  }, [offSetScroll]);

  const handleEndReached = useCallback(() => {
    if (end < itemsWithSuggestionSection.length) {
      setEnd(Math.min(itemsWithSuggestionSection.length, end + maxToLoadPerBatch));
    }
  }, [end, maxToLoadPerBatch]);

  return (
    <FlatList
      ref={flatListRef}
      refreshControl={refreshControl}
      data={dataItems}
      keyExtractor={(item, index) => item._name + index.toString()}
      renderItem={renderItem}
      initialNumToRender={initialNumToRender}
      maxToRenderPerBatch={maxToRenderPerBatch}
      onEndReached={handleEndReached}
      onEndReachedThreshold={1}
      contentContainerStyle={styles.contentContainer}
      onScroll={handleScroll}
      scrollEnabled={!isFullScreen}
      ListHeaderComponent={
        <>
          {hasBanner && isPromotionActive && (
            <View style={styles.bannerWrapper}>
              {/* Substitua Link por TouchableOpacity se navegação for necessária */}
              <Image
                source={{
                  uri:
                    isWeb && width > 500
                      ? PromotionArea?.desktop_image.url
                      : PromotionArea?.mobile_image.url,
                }}
                style={[
                  styles.bannerImage,
                  {
                    width: '100%',
                    height: isWeb ? (isSmallScreen ? 80 : 100) : 80,
                  },
                ]}
                resizeMode="cover"
              />
            </View>
          )}
        </>
      }
    />
  );
};

const styles = StyleSheet.create({
  itemWrapper: {
    marginBottom: calcSizeForDevice(15, 30),
  },
  bannerWrapper: {
    width: '100%',
  },
  bannerImage: {
    // width e height são definidos dinamicamente
  },
  contentContainer: {
    paddingBottom: 40,
  },
});

export default DynamicList;
